import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  useTheme,
  alpha,
  Divider,
  IconButton,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Today as TodayIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarTodayIcon,
  Clear as ClearIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { DateTimeRange } from './types';
import { format } from 'date-fns';

interface DateTimeRangeFilterProps {
  fieldType: string;
  value?: DateTimeRange;
  onChange: (value: DateTimeRange | null) => void;
}

const DateTimeRangeFilter: React.FC<DateTimeRangeFilterProps> = ({
  fieldType,
  value,
  onChange,
}) => {
  const theme = useTheme();
  const [mode, setMode] = useState<'before' | 'after' | 'between'>(value?.mode || 'after');
  const [startDate, setStartDate] = useState<Date | null>(value?.start || null);
  const [endDate, setEndDate] = useState<Date | null>(value?.end || null);

  // Handle mode change
  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: 'before' | 'after' | 'between' | null) => {
    if (newMode) {
      setMode(newMode);
      updateValue(newMode, startDate, endDate);
    }
  };

  // Update the value based on current state
  const updateValue = (currentMode: 'before' | 'after' | 'between', start: Date | null, end: Date | null) => {
    if (currentMode === 'before' && end) {
      onChange({ mode: 'before', end });
    } else if (currentMode === 'after' && start) {
      onChange({ mode: 'after', start });
    } else if (currentMode === 'between' && start && end) {
      onChange({ mode: 'between', start, end });
    } else {
      onChange(null);
    }
  };

  // Quick preset handlers
  const applyPreset = (preset: string) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'today':
        setMode('between');
        setStartDate(today);
        setEndDate(new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1));
        updateValue('between', today, new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1));
        break;
      case 'last7':
        setMode('after');
        const week = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        setStartDate(week);
        updateValue('after', week, null);
        break;
      case 'last30':
        setMode('after');
        const month = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        setStartDate(month);
        updateValue('after', month, null);
        break;
      case 'last90':
        setMode('after');
        const quarter = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
        setStartDate(quarter);
        updateValue('after', quarter, null);
        break;
    }
  };

  // Clear filter
  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    onChange(null);
  };

  // Get display label for the field
  const getFieldLabel = () => {
    const labels: Record<string, string> = {
      created_at: 'Created',
      updated_at: 'Updated',
      last_contacted_at: 'Last Contacted',
      last_email_from_contact: 'Email Received',
      last_email_to_contact: 'Email Sent',
      last_chat_from_contact: 'Chat Received',
      last_chat_to_contact: 'Chat Sent',
      last_sms_from_contact: 'SMS Received',
      last_sms_to_contact: 'SMS Sent',
      birthday: 'Birthday',
    };
    return labels[fieldType] || fieldType;
  };

  // Check if this is a date-only field
  const isDateOnlyField = fieldType === 'birthday';
  const DateComponent = isDateOnlyField ? DatePicker : DateTimePicker;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2, minWidth: 380 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DateRangeIcon fontSize="small" color="primary" />
            {getFieldLabel()} Filter
          </Typography>
          <IconButton size="small" onClick={handleClear} sx={{ ml: 1 }}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Quick Presets */}
        <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            label="Today"
            icon={<TodayIcon fontSize="small" />}
            onClick={() => applyPreset('today')}
            size="small"
            variant={mode === 'between' && startDate && endDate ? 'filled' : 'outlined'}
            color="primary"
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="Last 7 days"
            icon={<AccessTimeIcon fontSize="small" />}
            onClick={() => applyPreset('last7')}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="Last 30 days"
            icon={<CalendarTodayIcon fontSize="small" />}
            onClick={() => applyPreset('last30')}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="Last 90 days"
            icon={<CalendarTodayIcon fontSize="small" />}
            onClick={() => applyPreset('last90')}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ cursor: 'pointer' }}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />

        {/* Mode Selection */}
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="before">Before</ToggleButton>
          <ToggleButton value="after">After</ToggleButton>
          <ToggleButton value="between">Between</ToggleButton>
        </ToggleButtonGroup>

        {/* Date Inputs */}
        <Stack spacing={2}>
          {(mode === 'after' || mode === 'between') && (
            <DateComponent
              label={mode === 'between' ? 'Start Date' : 'After Date'}
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue);
                updateValue(mode, newValue, endDate);
              }}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  variant: 'outlined',
                },
              }}
            />
          )}

          {(mode === 'before' || mode === 'between') && (
            <DateComponent
              label={mode === 'between' ? 'End Date' : 'Before Date'}
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue);
                updateValue(mode, startDate, newValue);
              }}
              minDate={mode === 'between' ? startDate : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  variant: 'outlined',
                },
              }}
            />
          )}
        </Stack>

        {/* Current Filter Summary */}
        {value && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              borderRadius: 1,
              border: 1,
              borderColor: alpha(theme.palette.primary.main, 0.2),
            }}
          >
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              Active Filter: {getFieldLabel()} {value.mode}
              {value.start && ` ${format(value.start, isDateOnlyField ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm a')}`}
              {value.mode === 'between' && value.end && ` - ${format(value.end, isDateOnlyField ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm a')}`}
              {value.mode === 'before' && value.end && ` ${format(value.end, isDateOnlyField ? 'MMM d, yyyy' : 'MMM d, yyyy h:mm a')}`}
            </Typography>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DateTimeRangeFilter; 
